import {
  CampaignChannel,
  CampaignFilterClient,
  CampaignMode,
  DbBusinessCampaign,
  DbCampaignMedia,
  EmailCampaignRequest,
  MediaType,
} from "./models";
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  deleteDoc,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import { firebaseDb } from "../firebase/client-app";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";

export class CampaignService {
  private businessCampaignsRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_campaigns",
    );
  }

  private campaignMediaRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/campaign_media",
    );
  }

  async getAppTemplates(): Promise<any> {
    const response = await axiosInstance.get("/campaign/templates", {
      headers: { Authorization: getCookie("Authorization")?.toString() },
    });

    return response.data;
  }

  async addCampaignMedia(
    businessId: string,
    imageUrl: string,
    mediaId: string,
    type: MediaType,
    blurHash?: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbCampaignMedia> {
    const media: DbCampaignMedia = {
      url: imageUrl,
      media_id: mediaId,
      blur_hash: blurHash,
      type: type,
      created_at: Timestamp.now(),
    };

    const docRef = doc(this.campaignMediaRef(firestore, businessId));
    await setDoc(docRef, media);

    return media;
  }

  async getCampaignMedia(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbCampaignMedia[]> {
    const q = query(
      this.campaignMediaRef(firestore, businessId),
      orderBy("created_at", "desc"),
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => doc.data() as DbCampaignMedia);
  }

  async getBusinessCampaignById(
    businessId: string,
    campaignId: string,
    firestore: Firestore = firebaseDb,
  ) {
    const campaign = await getDoc(
      doc(this.businessCampaignsRef(firestore, businessId), campaignId),
    );
    return campaign.data() as DbBusinessCampaign;
  }

  async createCampaign(
    businessId: string,
    data: DbBusinessCampaign,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCampaign> {
    const campaignId = doc(this.businessCampaignsRef(firestore, businessId)).id;
    data.id = campaignId;
    await setDoc(
      doc(this.businessCampaignsRef(firestore, businessId), campaignId),
      data,
    );
    return data;
  }

  async updateCampaign(
    businessId: string,
    campaign: DbBusinessCampaign,
  ): Promise<void> {
    await updateDoc(
      doc(this.businessCampaignsRef(firebaseDb, businessId), campaign.id),
      {
        ...campaign,
      },
    );
  }

  async updateCampaignClientsAndStatus(
    businessId: string,
    campaignId: string,
    clients:
      | {
          client_id: string;
          created_at: Timestamp;
        }[]
      | CampaignFilterClient[],
    campaignStatus: string,
  ): Promise<void> {
    await setDoc(
      doc(this.businessCampaignsRef(firebaseDb, businessId), campaignId),
      { notified_users: clients, status: campaignStatus },
      { merge: true },
    );
    return;
  }

  async sendWhatsappCampaign(
    businessId: string,
    clients: {
      id: string;
      phone: string;
    }[],
    templateId: string,
    campaignId: string,
    params: string[],
    mode: CampaignMode,
    mediaURL?: string,
    mediaId?: string,
    postbackTexts?: any[],
  ) {
    const response = await axiosInstance.post(
      "/business/campaign",
      {
        business_id: businessId,
        template_id: templateId,
        campaign_id: campaignId,
        clients: clients,
        params: params,
        media_url: mediaURL,
        media_id: mediaId,
        postbackTexts: postbackTexts,
        mode: mode,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return response.data.success;
  }

  async sendEmailCampaign(
    businessId: string,
    clients: {
      id: string;
      email: string;
    }[],
    templateId: string,
    campaignId: string,
    mode: CampaignMode,
    emailSender?: string,
    emailSubject?: string,
    templateValues?: Record<string, string>,
  ) {
    const requestData: EmailCampaignRequest = {
      business_id: businessId,
      template_id: templateId,
      channel: CampaignChannel.Email,
      clients: clients,
      mode: mode,
    };

    if (mode === CampaignMode.Send && campaignId) {
      requestData.campaign_id = campaignId;
    }

    if (emailSender) {
      requestData.email_sender = emailSender;
    }

    if (emailSubject) {
      requestData.email_subject = emailSubject;
    }

    if (templateValues && Object.keys(templateValues).length > 0) {
      requestData.template_values = templateValues;
    }

    const response = await axiosInstance.post(
      "/business/email-campaign/send",
      requestData,
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return response.data.success;
  }

  async getBusinessCampaigns(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCampaign[]> {
    const campaignsQuery = query(
      this.businessCampaignsRef(firestore, businessId),
      orderBy("created_at", "desc"),
    );
    const campaignsSnapshot = await getDocs(campaignsQuery);

    return campaignsSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessCampaign,
    );
  }

  async deleteBusinessCampaignById(
    businessId: string,
    campaignId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    const campaignRef = doc(
      this.businessCampaignsRef(firestore, businessId),
      campaignId,
    );
    await deleteDoc(campaignRef);
  }

  async uploadWhatsappTemplateMedia(mediaURL?: string, mediaType?: string) {
    const response = await axiosInstance.post(
      "/campaign/media",
      {
        media_url: mediaURL,
        media_type: mediaType,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return response.data;
  }
}

export const campaignService = new CampaignService();
